<template>
  <div class="product-form-view-container">
    <div class="page-breadcrumb">
      <span>內容管理</span> <i class="fas fa-chevron-right separator"></i>
      <a href="#" @click.prevent="goBackToList" class="breadcrumb-link">商品管理</a>
      <i class="fas fa-chevron-right separator"></i>
      <span>{{ pageTitle }}</span>
    </div>

    <div v-if="isLoadingForm" class="loading-spinner">
      <p>{{ isEditingForm ? '載入商品資料中...' : '準備新增商品...' }}</p>
    </div>

    <BilingualContentTabs ref="contentTabs" resource-type="product" :resource-id="activeProductId" :source-revision="sourceRevision">
    <form @submit.prevent="handleSaveProductForm" v-if="!isLoadingForm" class="product-form-standalone">
      <h2 class="form-title">{{ pageTitle }}</h2>

      <div class="form-group">
        <label for="formProductName">商品名稱：</label>
        <input type="text" id="formProductName" v-model="productData.name" data-testid="source-name" required class="form-input" placeholder="輸入商品名稱">
      </div>

      <div class="form-group">
        <label for="formProductImageUpload">商品圖片：</label>
        <input type="file" id="formProductImageUpload" data-testid="source-image" @change="handleImageFileChangeOnForm" accept="image/png, image/jpeg, image/webp" class="form-input-file">
        <div v-if="imagePreviewUrlForm || productData.imageUrl" class="image-preview-container-form">
          <img :src="imagePreviewUrlForm || productData.imageUrl" alt="圖片預覽" class="image-preview-form">
          <button type="button" @click="removeSelectedImageOnForm" class="remove-image-btn" v-if="selectedImageFileForm || (isEditingForm && productData.imageUrl)">
            {{ isEditingForm && productData.imageUrl && !selectedImageFileForm ? '移除現有圖片' : '清除選擇' }}
          </button>
        </div>
        <small v-if="!imagePreviewUrlForm && !productData.imageUrl" class="image-upload-hint">
          (可選，建議尺寸 400 x 400，500KB 以下的 PNG/JPG/WEBP)
        </small>
      </div>

      <div class="form-group">
        <label for="formProductDescription">商品描述：</label>
        <Editor
            id="source-description"
            v-model="productData.description"
            :init="tinymceInitOptions"
            class="tinymce-editor-instance"
        />
        <small v-if="isEditorLoading" class="editor-loading-text"><i class="fas fa-spinner fa-spin"></i> 編輯器載入中...</small>
      </div>

      <div class="form-group">
        <label for="formProductCategory">商品分類：</label>
        <select id="formProductCategory" v-model="productData.categoryId" required class="form-input">
          <option disabled value="">請選擇分類</option>
          <option v-for="cat in availableCategoriesForm" :key="cat.id" :value="cat.id">
            {{ cat.name }}
          </option>
        </select>
      </div>

      <div class="form-group toggle-form-group">
        <label for="formProductFeatured" class="toggle-switch-label">設為精選：</label>
        <label class="switch">
          <input type="checkbox" id="formProductFeatured" v-model="productData.featured">
          <span class="slider round"></span>
        </label>
      </div>

      <div class="form-group toggle-form-group">
        <label for="formProductHot" class="toggle-switch-label">設為熱門：</label>
        <label class="switch">
          <input type="checkbox" id="formProductHot" v-model="productData.hot">
          <span class="slider round"></span>
        </label>
      </div>

      <div class="form-actions">
        <button type="button" @click="cancelForm" class="form-btn cancel-btn">取消</button>
        <button type="submit" data-testid="source-save" class="form-btn save-btn" :disabled="isSavingForm || isEditorLoading">
          <span v-if="isSavingForm"><i class="fas fa-spinner fa-spin"></i> 儲存中...</span>
          <span v-else><i class="fas fa-save"></i> {{ isEditingForm ? '更新商品' : '儲存商品' }}</span>
        </button>
      </div>
    </form>
    </BilingualContentTabs>
    <button type="button" @click="cancelForm" class="form-btn cancel-btn" style="margin-top:16px" data-testid="resource-back">返回商品列表</button>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import BilingualContentTabs from './BilingualContentTabs.vue';
import { adminError } from '@/utils/adminError';
import { guardBrowserClose } from '@/utils/unsavedChanges';
import { useToast } from 'vue-toastification';
import {
  createProduct,
  updateProduct,
  getProductById,
} from '@/api/product.api.js';
import { getAllCategories } from '@/api/category.api.js';
import {uploadProductImage, uploadProductRichtextImage} from '@/api/file.api.js';

// 導入 TinyMCE Vue component
import Editor from '@/components/LocalRichTextEditor.vue';

// eslint-disable-next-line no-undef
const props = defineProps({
  productId: {
    type: [Number, String, null],
    default: null,
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['form-saved', 'form-cancelled']);

const toast = useToast();

const savedProductId = ref(null);
const contentTabs = ref(null);
const sourceRevision = ref(0);
const sourceBaseline = ref('');
const activeProductId = computed(() => props.productId || savedProductId.value);
const isEditingForm = computed(() => !!activeProductId.value);
const pageTitle = computed(() => (isEditingForm.value ? '編輯商品' : '新增商品'));

const productData = ref({
  id: null, name: '', description: '', categoryId: '', categoryName: '',
  featured: false, hot: false, imageName: '', imageUrl: '',
});

const availableCategoriesForm = ref([]);
const isLoadingForm = ref(false);
const isSavingForm = ref(false);
const selectedImageFileForm = ref(null);
const imagePreviewUrlForm = ref('');
const isEditorLoading = ref(true); // 用於追蹤 TinyMCE 是否載入完成

// --- TinyMCE Configuration ---

const tinymceInitOptions = ref({
  valid_elements: '*[*]',
  extended_valid_elements: 'script[src|type|async|defer],style[type],link[href|rel]',
  valid_children: '+body[style|script|link]',
  allow_script_urls: true,
  language: 'zh_TW',
  plugins: 'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons',
  menubar: 'file edit view insert format tools table help',
  toolbar: 'undo redo | accordion accordionremove | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image media table | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl',
  toolbar_mode: 'sliding',
  height: 500,
  skin: 'oxide-dark',
  content_css: 'dark',
  font_family_formats: `
    Noto Sans TC='Noto Sans TC';
    微軟正黑體=Microsoft JhengHei;
    標楷體=DFKai-SB;
    新細明體=PMingLiU;
    Arial=arial,helvetica,sans-serif
  `,
  // 或者，提供一個自訂的 content_style 來更精確匹配您的頁面背景和文字顏色
  content_style: `
    body {
      background-color: #14141e; /* 編輯器內容區域背景色 (配合您的輸入框) */
      color: #e0e0e0; /* 編輯器內容文字顏色 */
      font-family: 'Noto Sans TC', 'Microsoft JhengHei', 'DFKai-SB', 'PMingLiU', sans-serif;
      line-height: 1.7;
    }
    a { color: #8be9fd; }
    img { max-width: 100%; height: auto; border-radius: 4px; }
    /* 您可以在這裡添加更多自訂樣式 */
  `,
  branding: false,
  promotion: false,
  image_advtab: true,
  // 圖片上傳處理
  images_upload_handler: async (blobInfo) => {
    try {
      const response = await uploadProductRichtextImage(blobInfo.blob());
      if (response && response.url) {
        return response.url; // ✅ TinyMCE 會處理這個 resolved promise
      } else {
        throw new Error('圖片上傳成功，但未收到有效的 URL。');
      }
    } catch (error) {
      const errorMessage = adminError(error, '圖片上傳失敗，請檢查檔案後重試。');
      throw new Error(errorMessage); // ✅ TinyMCE 會顯示這個錯誤
    }
  },
  file_picker_types: 'image media',
  init_instance_callback: (editor) => {
    isEditorLoading.value = false;
    // 如果是編輯模式且有內容，設定編輯器內容
    if (isEditingForm.value && productData.value.description) {
      editor.setContent(productData.value.description);
    }
  }
});

const resetFormFields = () => {
  productData.value = {
    id: null, name: '', description: '', categoryId: '', categoryName: '',
    featured: false, hot: false, imageName: '', imageUrl: '',
  };
  selectedImageFileForm.value = null;
  imagePreviewUrlForm.value = '';
  const fileInput = document.getElementById('formProductImageUpload');
  if (fileInput) fileInput.value = '';
  isEditorLoading.value = true; // 重置時準備重新載入編輯器
};

const fetchProductDetailsForForm = async () => {
  if (isEditingForm.value && props.productId) {
    isLoadingForm.value = true;
    isEditorLoading.value = true; // 開始載入數據，編輯器也視為載入中
    try {
      const fetchedProduct = await getProductById(activeProductId.value);
      productData.value = {
        ...fetchedProduct,
        description: fetchedProduct.description || '', // 確保 description 不是 null
        featured: !!fetchedProduct.featured,
        hot: !!fetchedProduct.hot,
        categoryId: fetchedProduct.categoryId || '',
      };
      imagePreviewUrlForm.value = '';
      selectedImageFileForm.value = null;
      sourceBaseline.value = JSON.stringify(productData.value);
    } catch (error) {
      console.error("獲取商品詳情失敗 (表單):", error);
      toast.error('無法載入商品資料進行編輯。');
      emit('form-cancelled');
    } finally {
      isLoadingForm.value = false;
      // isEditorLoading 會在 init_instance_callback 中設為 false
    }
  } else {
    resetFormFields();
    sourceBaseline.value = JSON.stringify(productData.value);
    // 新增模式，等 editor 渲染後，init_instance_callback 會設置 isEditorLoading
  }
};

const fetchAvailableCategoriesForForm = async () => {
  try {
    const data = await getAllCategories();
    availableCategoriesForm.value = data;
  } catch (error) {
    console.error("獲取分類選項失敗 (表單):", error);
    toast.error('無法載入商品分類選項。');
  }
};

watch(() => props.productId, () => {
  savedProductId.value = null;
  fetchProductDetailsForForm();
}, { immediate: true });

onMounted(async () => {
  await fetchAvailableCategoriesForForm();
});

const handleImageFileChangeOnForm = (event) => {
  // ... 此函數邏輯不變 ...
  const file = event.target.files[0];
  if (file) {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    const maxSize = 500 * 1024;
    if (!allowedTypes.includes(file.type)) {
      toast.error('檔案格式錯誤，僅支援 PNG / JPG / WEBP 圖片。');
      event.target.value = ''; return;
    }
    if (file.size > maxSize) {
      toast.error('圖片大小不得超過 500KB。');
      event.target.value = ''; return;
    }
    selectedImageFileForm.value = file;
    imagePreviewUrlForm.value = URL.createObjectURL(file);
    productData.value.imageName = file.name;
  }
};

const removeSelectedImageOnForm = () => {
  // ... 此函數邏輯不變 ...
  selectedImageFileForm.value = null;
  imagePreviewUrlForm.value = '';
  productData.value.imageUrl = '';
  productData.value.imageName = '';
  const fileInput = document.getElementById('formProductImageUpload');
  if (fileInput) fileInput.value = '';
};

const handleSaveProductForm = async () => {
  if (isEditorLoading.value) {
    toast.warning('編輯器仍在載入中，請稍候。');
    return;
  }
  if (!productData.value.name.trim() || !productData.value.categoryId) {
    toast.warning('商品名稱和分類為必填項！');
    return;
  }
  isSavingForm.value = true;
  // ... 後續的圖片上傳和表單提交邏輯不變 ...
  let finalImageUrl = productData.value.imageUrl;
  let finalImageName = productData.value.imageName;

  if (selectedImageFileForm.value) {
    try {
      const toastId = toast.info('圖片上傳中...', { timeout: false, id: 'form-image-uploading-id' });
      const uploadResult = await uploadProductImage(selectedImageFileForm.value);
      toast.dismiss(toastId);
      if (uploadResult && uploadResult.url) {
        finalImageUrl = uploadResult.url;
        finalImageName = finalImageUrl.substring(finalImageUrl.lastIndexOf('/') + 1);
      } else {
        throw new Error(uploadResult.error || '圖片上傳成功但未返回有效的 URL。');
      }
    } catch (error) {
      toast.dismiss('form-image-uploading-id');
      console.error("圖片上傳失敗 (表單):", error);
      const uploadErrorMessage = adminError(error, '圖片上傳操作失敗，請檢查檔案後重試。');
      toast.error(uploadErrorMessage);
      isSavingForm.value = false;
      return;
    }
  } else if (isEditingForm.value && !productData.value.imageUrl && props.productId) {
    finalImageUrl = '';
    finalImageName = '';
  }


  const selectedCategory = availableCategoriesForm.value.find(cat => cat.id === productData.value.categoryId);
  const dataToSubmit = {
    name: productData.value.name,
    description: productData.value.description, // description 已通過 v-model 更新
    categoryId: productData.value.categoryId,
    categoryName: selectedCategory ? selectedCategory.name : '',
    featured: !!productData.value.featured,
    hot: !!productData.value.hot,
    imageUrl: finalImageUrl,
    imageName: finalImageName,
  };

  try {
    if (isEditingForm.value) {
      await updateProduct(activeProductId.value, dataToSubmit);
      toast.success('商品更新成功！');
    } else {
      const created = await createProduct(dataToSubmit);
      savedProductId.value = created.id;
      toast.success('商品新增成功！');
    }
    productData.value.imageUrl = finalImageUrl;
    productData.value.imageName = finalImageName;
    selectedImageFileForm.value = null;
    imagePreviewUrlForm.value = '';
    sourceBaseline.value = JSON.stringify(productData.value);
    sourceRevision.value += 1;
  } catch (error) {
    console.error("保存商品失敗 (表單):", error);
    const errorMessage = adminError(error);
    toast.error(`保存商品失敗：${errorMessage}`);
  } finally {
    isSavingForm.value = false;
  }
};

const confirmSourceDiscard = () => sourceBaseline.value === JSON.stringify(productData.value) && !selectedImageFileForm.value || window.confirm('中文商品尚未儲存，確定要捨棄修改嗎？');
const cancelForm = () => {
  if (confirmSourceDiscard() && (!contentTabs.value || contentTabs.value.confirmDiscard())) emit('form-cancelled');
};
const goBackToList = cancelForm;
onBeforeRouteLeave(() => confirmSourceDiscard());
guardBrowserClose(() => sourceBaseline.value && (sourceBaseline.value !== JSON.stringify(productData.value) || !!selectedImageFileForm.value));
</script>

<style scoped>
/* ProductForm.vue 的樣式，可以從 ProductsView.vue 的 Modal 樣式遷移和調整 */
.product-form-view-container {
  padding: 20px;
  color: #c0c0c0;
}

.page-breadcrumb {
  font-size: 0.95em;
  color: #a0a0b0;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
}
.page-breadcrumb .separator { margin: 0 8px; font-size: 0.7em; }
.breadcrumb-link {
  color: #8be9fd;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;
}
.breadcrumb-link:hover {
  color: #a0eefe;
}

.product-form-standalone {
  background-color: rgba(30, 30, 40, 0.85);
  padding: 30px 40px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.4);
  box-shadow: 0 5px 25px rgba(0,0,0,0.4);
  margin: 0 auto;
}

.form-title {
  color: #ff8c69;
  margin-top: 0;
  margin-bottom: 30px;
  font-size: 1.8em;
  text-align: center;
  font-weight: 600;
}

.form-group {
  margin-bottom: 25px;
}
.form-group label:not(.switch):not(.toggle-switch-label) {
  display: block;
  margin-bottom: 8px;
  font-size: 0.95em;
  color: #a0a0b0;
  font-weight: 500;
}

.form-input, .form-input-file {
  width: 100%;
  padding: 10px 12px;
  background-color: rgba(20, 20, 30, 0.9);
  border: 1px solid rgba(160, 100, 220, 0.5);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.form-input-file {
  padding: 8px 12px;
  line-height: 1.5;
}
.form-input:focus, .form-input-file:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 0 3px rgba(255, 140, 105, 0.2);
}
.form-input.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.image-preview-container-form {
  margin-top: 10px;
  text-align: center;
  border: 1px dashed rgba(160, 100, 220, 0.3);
  padding: 10px;
  border-radius: 6px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.image-preview-form {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  display: block;
  margin-bottom: 10px;
}
.remove-image-btn {
  background: none;
  border: 1px solid #ff8080;
  color: #ff8080;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.8em;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 5px;
}
.remove-image-btn:hover {
  background-color: rgba(255, 85, 85, 0.15);
  color: #ff6666;
}
small.image-upload-hint {
  display: block;
  margin-top: 5px;
  font-size: 0.8em;
  color: #888e95;
}

.form-group.toggle-form-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 3px 0;
}
.toggle-switch-label {
  font-size: 0.9em;
  color: #a0a0b0;
  margin-right: 15px;
  line-height: 28px;
  white-space: nowrap;
}
.switch {
  position: relative; display: inline-block; width: 50px; height: 28px; flex-shrink: 0;
}
.switch input { opacity: 0; width: 0; height: 0; }
.slider {
  position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(80, 80, 90, 0.7); transition: .3s;
  border: 1px solid rgba(120, 120, 130, 0.5);
}
.slider:before {
  position: absolute; content: ""; height: 20px; width: 20px;
  left: 3px; bottom: 3px; background-color: #e0e0e0; transition: .3s;
}
#formProductFeatured:checked + .slider { background-color: #50fa7b; border-color: #3ddc67; }
#formProductHot:checked + .slider { background-color: #ff5555; border-color: #dd4444; }
input:focus + .slider { box-shadow: 0 0 8px rgba(139, 233, 253, 0.4); }
#formProductFeatured:checked:focus + .slider { box-shadow: 0 0 8px rgba(80, 250, 123, 0.6); }
#formProductHot:checked:focus + .slider { box-shadow: 0 0 8px rgba(255, 85, 85, 0.6); }
input:checked + .slider:before { transform: translateX(22px); background-color: #1a1a1a; }
#formProductHot:checked + .slider:before { background-color: #ffffff; }
.slider.round { border-radius: 28px; }
.slider.round:before { border-radius: 50%; }

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}
.form-btn {
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.form-btn.cancel-btn {
  background-color: rgba(80, 80, 90, 0.8);
  color: #c0c0c0;
  border: 1px solid rgba(100,100,110,0.8);
}
.form-btn.cancel-btn:hover {
  background-color: rgba(90, 90, 100, 0.9);
}
.form-btn.save-btn {
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
}
.form-btn.save-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.2);
}
.form-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.form-btn i { margin-right: 6px; }

.loading-spinner {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.form-input.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.editor-loading-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #a0a0b0;
  font-size: 0.9em;
  z-index: 10;
}

/* TinyMCE Dark Theme Styles (與 ArticleEdit 一致) */
.tinymce-editor-instance {
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid rgba(160, 100, 220, 0.5); /* 調整邊框顏色以匹配輸入框 */
}

:deep(.tox .tox-toolbar__primary, .tox .tox-toolbar__secondary) {
  background-color: rgba(35, 35, 50, 0.8) !important;
}
:deep(.tox .tox-edit-area__iframe) {
  background-color: #14141e !important;
}
:deep(.tox .tox-statusbar) {
  background-color: rgba(35, 35, 50, 0.8) !important;
  color: #a0a0b0 !important;
  border-top: 1px solid rgba(160, 100, 220, 0.4) !important;
}
:deep(.tox .tox-statusbar a) {
  color: #8be9fd !important;
}
:deep(.tox .tox-tbtn svg) {
  fill: #a0a0b0 !important;
}
:deep(.tox .tox-tbtn:hover svg) {
  fill: #e0e0e0 !important;
}
:deep(.tox .tox-mbtn, .tox .tox-tbtn) {
  color: #a0a0b0 !important;
}
:deep(.tox .tox-mbtn:hover, .tox .tox-tbtn--enabled, .tox .tox-tbtn:focus) {
  background-color: rgba(60, 60, 75, 0.9) !important;
  color: #e0e0e0 !important;
}
:deep(.tox .tox-tbtn--select) {
  color: #a0a0b0 !important;
}
:deep(.tox .tox-tbtn--select .tox-tbtn__select-label) {
  color: #a0a0b0 !important;
}
</style>
